// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';

import localizedStrings from 'util/localizedStrings';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import {
  getDiagnosticDataStatus,
  getIsSendingDiagnosticData,
  getServerErrorStatuses,
  getServerStartupFailureReason,
} from 'state/selectors';
import { DIAGNOSTIC_DATA_PANEL_ID } from 'util/constants';
import { showSingleButtonAlert } from 'portfolio-shared/state/reducers/modalPromptReducer';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import DiagnosticDataDialog from 'view/App/DialogPanels/DiagnosticDataDialog';
import type { Dispatch, ServerStatusErrorMessage } from 'state/types';

type Props = {
  diagnosticDataStatus: string,
  enabledMediaEnginesCount: boolean,
  errorStatuses: Array<ServerStatusErrorMessage>,
  intl: IntlShape,
  isSendingDiagnosticData: boolean,
  showDialogPanel: (string, ?Object) => void,
  showSingleButtonAlert: (Object, Object) => void,
  startupFailureReason: ?string,
};

type State = {
  showVaultErrorPrompt: boolean,
};

class Diagnostics extends Component<Props, State> {
  getDiagnosticDataLabel: Function;
  getDiagnosticDataStyle: Function;
  handleSendDiagnosticData: Function;

  constructor() {
    super();
    this.state = { showVaultErrorPrompt: true };
    this.handleSendDiagnosticData = this.handleSendDiagnosticData.bind(this);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const vaultErrorMessage: ?ServerStatusErrorMessage = this.props.errorStatuses.find(
      e => e.id === 'VaultFolderNotFound'
    );
    if (vaultErrorMessage && this.state.showVaultErrorPrompt) {
      this.setState({ showVaultErrorPrompt: false });
      this.props.showSingleButtonAlert(
        localizedStrings.NO_VAULT_FOLDER,
        vaultErrorMessage.parameters
      );
    }
  }

  handleSendDiagnosticData() {
    this.props.showDialogPanel(DIAGNOSTIC_DATA_PANEL_ID);
  }

  getDiagnosticDataLabel() {
    const { diagnosticDataStatus, intl, isSendingDiagnosticData } = this.props;

    if (isSendingDiagnosticData) {
      return (
        <FormattedMessage {...localizedStrings.DIAGNOSTIC_STATUS_SENDING} />
      );
    }

    if (diagnosticDataStatus !== '') {
      const status = diagnosticDataStatus.split(' ');
      if (status.length > 1) {
        const date = status[1];
        let message = localizedStrings.DIAGNOSTIC_STATUS_ERROR;
        if (diagnosticDataStatus.includes('success')) {
          message = localizedStrings.DIAGNOSTIC_STATUS_SUCCESS;
        }
        return (
          <FormattedMessage
            {...message}
            values={{
              date: intl.formatDate(date) + ' ' + intl.formatTime(date),
            }}
          />
        );
      }
    }
    return diagnosticDataStatus;
  }

  getDiagnosticDataClassName() {
    const { diagnosticDataStatus, isSendingDiagnosticData } = this.props;
    if (isSendingDiagnosticData) {
      return 'has-text-primary has-text-weight-bold';
    } else if (diagnosticDataStatus !== '') {
      if (this.props.diagnosticDataStatus.includes('success')) {
        return 'has-text-black';
      } else {
        return 'has-text-danger';
      }
    }
    return '';
  }

  render() {
    const { errorStatuses, startupFailureReason } = this.props;
    const allGood = !startupFailureReason && !errorStatuses.length;
    return (
      <div className="box">
        <div className="title is-4">
          <FormattedMessage {...localizedStrings.DIAGNOSTICS_TITLE} />
        </div>
        {allGood && (
          <span className="is-indented">
            <i className="fa fa-check-circle fa-lg has-text-primary licenseIcon" />
            &nbsp;
            <FormattedMessage {...localizedStrings.DIAGNOSTICS_NO_PROBLEMS} />
          </span>
        )}
        <div className="status-page-grid">
          {/* Startup failure message */}
          {startupFailureReason && (
            <div>
              <FormattedMessage
                {...localizedStrings.STATUS_MSG_STARTUP_FAILED}
              />
            </div>
          )}
          {startupFailureReason && (
            <div
              className="status-page-grid-item-right error"
              data-test-name="startup-error"
            >
              {startupFailureReason}
            </div>
          )}
          {/* server problems */}
          {errorStatuses.length !== 0 && (
            <div className="error">
              <FormattedMessage
                {...localizedStrings.STATUS_PROBLEMS_DETECTED}
              />
            </div>
          )}
          {errorStatuses.length !== 0 && (
            <div
              className="status-page-grid-item-right"
              style={{ height: '100px', overflow: 'auto' }}
            >
              {errorStatuses.map((e: ServerStatusErrorMessage) => (
                <div className="error">
                  <FormattedMessage
                    {...localizedStrings[e.id]}
                    values={
                      e.parameters &&
                      e.parameters.reduce((acc, v) => {
                        acc[v.name] = v.value;
                        return acc;
                      }, {})
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="vertical-spacer-large" />
        {/* Send Diagnostic Data */}
        <button
          className="button is-text"
          onClick={this.handleSendDiagnosticData}
          data-test-name="send-diag-data"
        >
          <FormattedMessage {...localizedStrings.DIAGNOSTIC_DATA_TITLE} />
        </button>
        {/* Diagnostic Data Status */}
        <div
          className={`status-page-diagnostic-data-status ${this.getDiagnosticDataClassName()}`}
          data-test-name="diag-data-status"
        >
          {this.getDiagnosticDataLabel()}
        </div>
        <DiagnosticDataDialog />
      </div>
    );
  }
}

let wrappedForm = injectIntl(Diagnostics);

export default (wrappedForm = connect(
  state => ({
    diagnosticDataStatus: getDiagnosticDataStatus(state),
    errorStatuses: getServerErrorStatuses(state),
    isSendingDiagnosticData: getIsSendingDiagnosticData(state),
    startupFailureReason: getServerStartupFailureReason(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      showDialogPanel,
      showSingleButtonAlert,
    })
)(wrappedForm));



// WEBPACK FOOTER //
// ./src/view/Pages/StatusPage/Diagnostics.js